import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ROUTING_TRAMITE_PENSION } from "@constants/url.constant";
import { StorageService } from "@services/storage/storage.service";
import { PersonalData } from '../../../shared/common/interfaces/personal.interface';
import { Beneficiary } from "@shared/common/interfaces/beneficiario.interface";
import { firstValueFrom } from "rxjs";
import { GtmService } from "@services/gtm/gtm.service";
import { TramitePensionService } from "src/app/tramite-pension/services/tramite-pension.service";
import { PiDocumentStatusComponent, DocumentoCacheado } from "../pi-document-status/pi-document-status.component";
import { CommonModule } from "@angular/common";
import { DsButtonV2Component, DsCardComponent, DsInlineAlertComponent, DsSkeletonComponent } from "@customdesignsystem";
  
@Component({
  selector: "app-pi-document-upload",
  templateUrl: "./pi-document-upload.component.html",
  styleUrls: ["./pi-document-upload.component.scss"],
  imports: [CommonModule, PiDocumentStatusComponent, DsCardComponent, DsSkeletonComponent, DsButtonV2Component, DsInlineAlertComponent],
  standalone: true
})
export class PiDocumentUploadComponent implements OnInit {
  private readonly CODIGOS_PARENTEZCO_EXCLUIDOS_BENEFICIARIOS: number[] = [
    10, 11, 12, 13, 14, 15, 16, 22, 23, 24, 25, 95
  ];

  private readonly CATEGORIAS_ERROR_SERVICIO: Array<{
    prioridad: number;
    palabrasClaves: string[];
    mensaje: string;
  }> = [
      {
        prioridad: 1,
        palabrasClaves: ['calidad', 'legible', 'extraer datos', 'campos requeridos'],
        mensaje: 'El documento no es legible. Por favor, usa una imagen más nítida e intenta nuevamente.',
      },
      {
        prioridad: 2,
        palabrasClaves: ['recurso no se encuentra', 'Document AI'],
        mensaje: 'Hubo un error al cargar este documento. Por favor, inténtalo nuevamente.',
      },
      {
        prioridad: 3,
        palabrasClaves: ['no coincide con los datos'],
        mensaje: 'El documento no coincide con los datos registrados. Por favor, revísalo e intenta nuevamente.',
      },
    ];

  errorSize = false;
  typeError = false;
  typeFile: string;
  isCancelQuery: boolean = false;
  loadingPage: boolean = true;
  loadingBeneficiarios: boolean = true;

  status: any = {
    nacimiento: "without_uploading_document",
    "nacimiento-conyuge": "without_uploading_document",
    matrimonio: "without_uploading_document",
    "union-civil": "without_uploading_document",
    "cedula-de-identidad": "without_uploading_document",
  };

  errorMessages: Record<string, string> = {};

  public conyuge: boolean = false;
  public convivienteCivil: boolean = false;
  public beneficiarios: Beneficiary[] = [];
  public beneficiaryDocumentKeys: string[] = [];

  /** Emite el estado de deshabilitación del botón Continuar hacia la página contenedora. */
  @Output() btnDisableChange = new EventEmitter<boolean>();
  private ultimoBtnDisableEmitido: boolean | null = null;

  /** Caché de documentos (nombre, preview, id de upload) para restaurar al navegar. */
  private readonly DOC_CACHE_PREFIX = "tp_invalidez_doc_";
  documentosCache: Record<string, DocumentoCacheado | null> = {};

  constructor(    
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly tramitePension: TramitePensionService,    
    private readonly storageService: StorageService,
    private readonly gtmService: GtmService,
  ) { }

  ngOnInit(): void {    
    void this.inicializarVista();
  }

  private async inicializarVista(): Promise<void> {
    try {      
      await this.evaluaHabilitarCertificadosSegunEstadoCivil();
      await this.evaluaHabilitarCertificadosSegunBeneficiarios();
      this.inicializarStatusDesdeStorage();
      await this.restaurarDocumentosDesdeCache();
    } finally {
      this.loadingPage = false;
      this.emitirEstadoBoton();
    }
  }

  goBack() {
    this.router.navigate([`../${ROUTING_TRAMITE_PENSION.VIA_PAGO}`], {
      relativeTo: this.activatedRoute,
    });
  }

  onFileSelected(payload: any): void {
    const { documentType: documentTypeKey, fileStringBase64, fileName, mimeType } = payload;
    const apiDocumentType = this.obtenerTipoDocumentoApi(documentTypeKey);
    const beneficiaryRut = this.obtenerRutBeneficiarioPorTipoDocumento(documentTypeKey);
    this.isCancelQuery = false;
    this.errorMessages[documentTypeKey] = "";
    this.status[documentTypeKey] = "VALIDATION_IN_PROGRESS";
    this.emitirEstadoBoton();

    void this.guardarCacheDocumento(documentTypeKey, {
      status: "VALIDATION_IN_PROGRESS",
      fileName: fileName ?? "",
      mimeType: mimeType ?? "",
      fileBase64: fileStringBase64,
    });

    this.tramitePension.uploadDocument({
      documentType: apiDocumentType,
      fileStringBase64,
      ...(beneficiaryRut ? { beneficiaryRut } : {}),
    }).subscribe({
      next: (response: any) => {
        const id = response?.data;
        this.status[documentTypeKey] = response?.status === 201 ? "VALIDATION_IN_PROGRESS" : null;
        this.emitirEstadoBoton();
        void this.actualizarCacheDocumento(documentTypeKey, { uploadId: id });
        setTimeout(() => this.recursiveDataFetch(documentTypeKey, apiDocumentType, id), 3000);
      },
      error: (err: any) => {
        if (this.isCancelQuery) {
          this.status[documentTypeKey] = "without_uploading_document";
          void this.eliminarCacheDocumento(documentTypeKey);
        } else {
          this.status[documentTypeKey] = "ERROR";
          this.errorMessages[documentTypeKey] =
            'El documento no coincide con los datos registrados. Por favor, revísalo e intenta nuevamente.';
          void this.actualizarCacheDocumento(documentTypeKey, { status: "ERROR" });
        }
        this.emitirEstadoBoton();
      },
    });
  }

  recursiveDataFetch(documentTypeKey: string, apiDocumentType: string, id: string): void {
    this.tramitePension.getDocumentStatus(apiDocumentType, id).subscribe({
      next: (response: any) => {
        const { status } = response.data;
        if (status === "VALIDATION_IN_PROGRESS" && !this.isCancelQuery) {
          this.status[documentTypeKey] = status;
          setTimeout(() => this.recursiveDataFetch(documentTypeKey, apiDocumentType, id), 3000);
        } else {
          this.status[documentTypeKey] = status;
          if (status === "SUCCESS") {
            this.errorMessages[documentTypeKey] = "";
            sessionStorage.setItem(documentTypeKey, "SUCCESS");
          }
          if (status === "ERROR") {
            this.errorMessages[documentTypeKey] = this.obtenerMensajeErrorDesdeServicio(response?.data?.errors);
          }
          void this.actualizarCacheDocumento(documentTypeKey, { status });
          this.emitirEstadoBoton();
        }
      },
      error: (error) => {
        console.error("Error al obtener el servico", error);
      }
    });
  }

  setDocumentType(type: string) {
    this.typeFile = type;
  }

  async continue() {
    try {
      this.gtmService.trackClick('button', 'Continuar', 'boton_principal', '/pensions/tramite-pensiones/solicitud/confirmar-datos');
      await this.router.navigate([`../${ROUTING_TRAMITE_PENSION.CONFIRMAR_DATOS}`], {
        relativeTo: this.activatedRoute,
      });
    } catch (error) {
      console.error("Error al navegar:", error);
    }
  }

  cancelQuery(event: boolean, documentType: string): void {
    this.isCancelQuery = event;
    this.status[documentType] = "without_uploading_document";
    this.errorMessages[documentType] = "";
    void this.eliminarCacheDocumento(documentType);
    this.emitirEstadoBoton();
  }

  delDocument(payload: any): void {
    this.status[payload.documentType] = 'without_uploading_document';
    this.errorMessages[payload.documentType] = "";
    sessionStorage.setItem(payload.documentType, "without_uploading_document");
    void this.eliminarCacheDocumento(payload.documentType);
    this.emitirEstadoBoton();
  }

  get btnDisable(): boolean {
    const documentosBaseCompletos =
      this.status.nacimiento === "SUCCESS" &&
      this.status["cedula-de-identidad"] === "SUCCESS";

    const documentosConyugalesCompletos =
      (!this.conyuge && !this.convivienteCivil) ||
      (
        this.status["nacimiento-conyuge"] === "SUCCESS" &&
        (!this.conyuge || this.status.matrimonio === "SUCCESS") &&
        (!this.convivienteCivil || this.status["union-civil"] === "SUCCESS")
      );

    const documentosBeneficiariosCompletos = this.beneficiaryDocumentKeys.every(
      (key) => this.status[key] === "SUCCESS"
    );

    return !(documentosBaseCompletos && documentosConyugalesCompletos && documentosBeneficiariosCompletos);
  }

  /** Notifica a la página contenedora solo cuando el valor de btnDisable cambia. */
  private emitirEstadoBoton(): void {
    const valorActual = this.btnDisable;
    if (valorActual !== this.ultimoBtnDisableEmitido) {
      this.ultimoBtnDisableEmitido = valorActual;
      this.btnDisableChange.emit(valorActual);
    }
  }

  private async evaluaHabilitarCertificadosSegunEstadoCivil() {
    let personalData: PersonalData = await this.storageService.get('tp_personal_data');
    console.log("evaluaHabilitarCertificadosSegunEstadoCivil : ", personalData);
    if (personalData?.codigoEstadoCivil === 'C') {
      this.conyuge = true;
    } else if (personalData?.codigoEstadoCivil === 'P') {
      this.convivienteCivil = true;
    } else {
      this.conyuge = false;
      this.convivienteCivil = false;
    }
  }

  private async evaluaHabilitarCertificadosSegunBeneficiarios(): Promise<void> {
    try {
      const beneficiarios = await firstValueFrom(this.tramitePension.getBeneficiaries());
      console.log("evaluaHabilitarCertificadosSegunBeneficiarios : ", beneficiarios);
      this.beneficiarios = (beneficiarios ?? []).filter(
        (beneficiario) =>
          !this.CODIGOS_PARENTEZCO_EXCLUIDOS_BENEFICIARIOS.includes(
            Number(beneficiario.codigoParentesco)
          )
      );
      this.beneficiaryDocumentKeys = this.beneficiarios.map((_, index) => `nacimiento-beneficiario-${index + 1}`);
      this.beneficiaryDocumentKeys.forEach((key) => {
        this.status[key] = "without_uploading_document";
      });
      this.loadingBeneficiarios = false;
    } catch {
      this.beneficiarios = [];
      this.beneficiaryDocumentKeys = [];
      this.loadingBeneficiarios = false;
    }
  }

  private inicializarStatusDesdeStorage(): void {
    Object.keys(this.status).forEach((clave) => {
      const response = sessionStorage.getItem(clave);
      this.status[clave] = response ?? "without_uploading_document";
      if (!response) {
        sessionStorage.setItem(clave, "without_uploading_document");
      }
    });
  }

  /** Restaura documentos cacheados (status, nombre, preview) y retoma polling pendiente. */
  private async restaurarDocumentosDesdeCache(): Promise<void> {
    const claves = Object.keys(this.status);
    await Promise.all(claves.map(async (clave) => {
      const cache: DocumentoCacheado | null = await this.storageService.get(this.DOC_CACHE_PREFIX + clave);
      if (!cache) {
        return;
      }
      this.documentosCache[clave] = cache;
      this.status[clave] = cache.status;

      if (cache.status === "VALIDATION_IN_PROGRESS") {
        if (cache.uploadId) {
          // El upload quedó en curso al salir de la página: se retoma el polling
          this.recursiveDataFetch(clave, this.obtenerTipoDocumentoApi(clave), cache.uploadId);
        } else {
          // Salió antes de obtener el id del upload: no se puede retomar, se descarta
          this.status[clave] = "without_uploading_document";
          await this.eliminarCacheDocumento(clave);
        }
      }
    }));
  }

  private async guardarCacheDocumento(clave: string, cache: DocumentoCacheado): Promise<void> {
    this.documentosCache[clave] = cache;
    await this.storageService.set(this.DOC_CACHE_PREFIX + clave, cache);
  }

  private async actualizarCacheDocumento(clave: string, cambios: Partial<DocumentoCacheado>): Promise<void> {
    const actual = this.documentosCache[clave] ?? await this.storageService.get(this.DOC_CACHE_PREFIX + clave);
    if (!actual) {
      return;
    }
    await this.guardarCacheDocumento(clave, { ...actual, ...cambios });
  }

  private async eliminarCacheDocumento(clave: string): Promise<void> {
    this.documentosCache[clave] = null;
    await this.storageService.remove(this.DOC_CACHE_PREFIX + clave);
  }

  private obtenerTipoDocumentoApi(documentTypeKey: string): string {
    if (documentTypeKey.startsWith("nacimiento-beneficiario-")) {
      return "nacimiento-beneficiario";
    }
    return documentTypeKey;
  }

  private obtenerRutBeneficiarioPorTipoDocumento(documentTypeKey: string): string | null {
    if (!documentTypeKey.startsWith("nacimiento-beneficiario-")) {
      return null;
    }

    const beneficiaryIndex = Number(documentTypeKey.split("nacimiento-beneficiario-")[1]) - 1;
    if (Number.isNaN(beneficiaryIndex) || beneficiaryIndex < 0) {
      return null;
    }

    return this.beneficiarios[beneficiaryIndex]?.idBeneficiario ?? null;
  }

  private obtenerMensajeErrorDesdeServicio(errors: unknown): string {
    if (!Array.isArray(errors) || errors.length === 0) {
      return 'El documento no coincide con los datos registrados. Por favor, revísalo e intenta nuevamente.';
    }

    const erroresServicio = errors
      .filter((item): item is string => typeof item === "string")
      .map(err => err.toLowerCase());

    for (const categoria of this.CATEGORIAS_ERROR_SERVICIO) {
      if (erroresServicio.some(err => categoria.palabrasClaves.some(palabra => err.includes(palabra.toLowerCase())))) {
        return categoria.mensaje;
      }
    }

    return 'El documento no coincide con los datos registrados. Por favor, revísalo e intenta nuevamente.';
  }

  getNombreCompletoBeneficiario(beneficiario: Beneficiary): string {
    const nombres = [
      beneficiario.nombres,
      beneficiario.apellidoPaterno,
      beneficiario.apellidoMaterno,
    ].filter(Boolean);
    return nombres.join(" ");
  }

  abrirRegistroCivil(event: Event): void {
    event.preventDefault();

    this.gtmService.trackClick('link', 'www.registrocivil.cl', 'link_externo', 'www.registrocivil.cl');
    window.open(
      'https://www.registrocivil.cl/',
      '_blank',
      'noopener,noreferrer'
    );
  }

}
